import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { schedulingApi } from "../../services/schedulingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { StoreSelect } from "../../components/StoreSelect";
import { useLocalization } from "../../hooks/useLocalization";

const EMPTY_FORM = { store_location: null, date: "", operating_start: "", operating_end: "", category: "NORMAL", notes: "", estimated_labor_budget: "" };

export default function StaffingPlanPage() {
  const { t } = useLocalization();
  const { canManageShiftTemplates, canViewScheduling } = usePermissions();
  const [plans, setPlans] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const load = () => schedulingApi.listStaffingPlans(null, month).then((r) => setPlans(r.data)).catch(() => {});

  useEffect(() => {
    if (!canViewScheduling) return;
    load();
  }, [canViewScheduling, month]);

  if (!canViewScheduling) return <p className="text-sm text-gray-500" data-testid="staffing-plan-access-denied">{t("common.accessDenied")}</p>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.store_location) { setError(t("mgmtScheduling.selectStoreError")); return; }
    try {
      await schedulingApi.upsertStaffingPlan({
        ...form,
        estimated_labor_budget: form.estimated_labor_budget ? Number(form.estimated_labor_budget) : null,
        time_blocks: [],
      });
      toast.success(t("mgmtScheduling.planSaved"));
      setForm((f) => ({ ...f, date: "", notes: "" }));
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-6xl space-y-4" data-testid="staffing-plan-page">
      <PageHelpBanner pageKey="staffing-plan" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtScheduling.staffingHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtScheduling.staffingTitle")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtScheduling.staffingSubtitle")}</p>
      </div>

      {canManageShiftTemplates && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="staffing-plan-form">
          <div className="grid grid-cols-2 gap-3">
            <StoreSelect testId="plan-store-input" value={form.store_location} onChange={(v) => setForm((f) => ({ ...f, store_location: v }))} className="px-3 py-2" />
            <input data-testid="plan-date-input" required type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="plan-operating-start-input" type="time" placeholder={t("mgmtScheduling.openingTimePlaceholder")} value={form.operating_start} onChange={(e) => setForm((f) => ({ ...f, operating_start: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="plan-operating-end-input" type="time" placeholder={t("mgmtScheduling.closingTimePlaceholder")} value={form.operating_end} onChange={(e) => setForm((f) => ({ ...f, operating_end: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <select data-testid="plan-category-select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">
              {["NORMAL", "SPECIAL_EVENT", "LARGE_ORDER", "HOLIDAY"].map((c) => <option key={c} value={c}>{c.replaceAll("_", " ")}</option>)}
            </select>
            <input data-testid="plan-labor-budget-input" type="number" placeholder={t("mgmtScheduling.laborBudgetPlaceholder")} value={form.estimated_labor_budget} onChange={(e) => setForm((f) => ({ ...f, estimated_labor_budget: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          <textarea data-testid="plan-notes-input" placeholder={t("mgmtScheduling.notesPlaceholder")} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" rows={2} />
          {error && <p className="text-sm text-red-500" data-testid="staffing-plan-form-error">{error}</p>}
          <button data-testid="submit-staffing-plan-button" type="submit" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">{t("mgmtScheduling.savePlan")}</button>
        </form>
      )}

      <input data-testid="staffing-plan-month-filter" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("common.date")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colStore")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colHours")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colCategory")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colBudget")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colNotes")}</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`staffing-plan-row-${p.id}`}>
                <td className="px-4 py-3">{p.date}</td>
                <td className="px-4 py-3">{p.store_location}</td>
                <td className="px-4 py-3 text-xs">{p.operating_start || "-"} - {p.operating_end || "-"}</td>
                <td className="px-4 py-3 text-xs">{p.category}</td>
                <td className="px-4 py-3 text-xs">{p.estimated_labor_budget ? `\u00a5${p.estimated_labor_budget}` : "-"}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{p.notes || "-"}</td>
              </tr>
            ))}
            {plans.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">{t("mgmtScheduling.noPlans")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
